// src/components/common/ImageSelector.js
import React, { useState, useEffect } from "react";
import { Button, Image } from "react-bootstrap";
import { useData } from "../../providers/dataProvider";
import ImageSelectionModal from "./ImageSelectionModal";

/**
 * A reusable component for selecting and previewing images
 *
 * @param {Object} props
 * @param {string} props.value - Current image key/path value
 * @param {function} props.onChange - Function to call when image is selected
 * @param {string} props.label - Label for the selector
 * @param {boolean} props.required - Whether an image is required
 */
const ImageSelector = ({
  value,
  onChange,
  label = "Image",
  required = false,
}) => {
  const { getImageUrl, getImageByKey } = useData();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imageInfo, setImageInfo] = useState(null);

  // Update the preview URL when the value changes
  useEffect(() => {
    const loadImagePreview = async () => {
      if (value) {
        try {
          // Use direct S3 URL from our new implementation
          const url = getImageUrl(value);
          setPreviewUrl(url);

          // Get additional image info from local state
          const imgInfo = getImageByKey(value);
          setImageInfo(imgInfo);
        } catch (error) {
          console.error("Error loading image preview:", error);
          setPreviewUrl(null);
        }
      } else {
        setPreviewUrl(null);
        setImageInfo(null);
      }
    };

    loadImagePreview();
  }, [value, getImageUrl, getImageByKey]);

  const handleImageSelect = (imageKey) => {
    onChange(imageKey);
    setShowModal(false);
  };

  const handleClearImage = () => {
    onChange("");
  };

  return (
    <div className="image-selector mb-3">
      <label className="form-label">
        {label}
        {required && <span className="text-danger">*</span>}
      </label>

      <div className="d-flex align-items-center">
        {previewUrl && (
          <div className="me-3">
            <Image
              src={previewUrl}
              alt={imageInfo?.description || "Selected image"}
              thumbnail
              width={100}
              height={100}
              style={{ objectFit: "cover" }}
            />
          </div>
        )}
        <div className="flex-grow-1">
          <Button
            variant="outline-light"
            onClick={() => setShowModal(true)}
            className="w-100"
          >
            {value ? "Change Image" : "Select Image"}
          </Button>
          {value && (
            <Button
              variant="outline-danger"
              size="sm"
              className="mt-2 w-100"
              onClick={handleClearImage}
            >
              Remove Image
            </Button>
          )}
        </div>
      </div>

      {value && imageInfo && (
        <div className="mt-2">
          <small className="text-muted">
            Selected: {imageInfo.description || value.split("/").pop()}
          </small>
        </div>
      )}

      <ImageSelectionModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSelect={handleImageSelect}
        currentImage={value}
      />
    </div>
  );
};

export default ImageSelector;
